import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { CategoryLeaf, CategoryBranch, Category } from "../types/catalog";
import { ProductListing } from "./product_listing";
import { ProductListingGridElement } from "./product_listing_grid";
import { ReactComponent as ArrowRightIcon } from "images/icons/arrow-right.svg";
import { useCategoryObserver, useStorefrontConfig } from "../types/storefront";
import { NavigateCatalog, useNavigationState } from "../types/navigation";
import * as CM from "src/storefront/types/cart";

const PREVIEW_AMOUNT = 12

export const CategoryPreviewList = ({
  categories,
  addToCart,
  removeFromCart,
  updateCart
}: {
  categories: Category[];
  addToCart?: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
  removeFromCart: (itemToRemove: CM.CartItem) => Promise<CM.Cart>;
  updateCart: (updatedCartItem: CM.CartItem) => Promise<CM.Cart>;
}) => {
  return categories.map((category) => {
    return <CategoryPreview key={category.keyPath()} category={category} addToCart={addToCart} removeFromCart={removeFromCart} updateCart={updateCart} />
  });
}

const CategoryPreview = ({
  category,
  addToCart,
  removeFromCart,
  updateCart
 }: {
  category: Category;
  addToCart?: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
  removeFromCart: (itemToRemove: CM.CartItem) => Promise<CM.Cart>;
  updateCart: (updatedCartItem: CM.CartItem) => Promise<CM.Cart>;
}) => {
  const storefrontConfig = useStorefrontConfig();
  const elementRef = useRef(null);
  const observer = useCategoryObserver();
  const [_navigationState, updateNavigation] = useNavigationState();

  const onCategoryNavigate = useCallback((e: React.MouseEvent<HTMLButtonElement>, newCategory: Category) => {
    e.preventDefault();

    updateNavigation(NavigateCatalog.action(newCategory.fullKeyPath()));
  }, [updateNavigation]);

  useEffect(() => {
    const element = elementRef.current;

    if (element != null && observer != null)
      observer.observe(element);

    return () => {
      if (element != null && observer != null)
        observer.unobserve(element);
    }
  }, [elementRef, observer]);

  const products = category.getProductPreview(PREVIEW_AMOUNT).map((product) => {
    if (storefrontConfig.hideAgeRestrictedItems && product.restrictions?.includes("age_r18")) return null;

    if (storefrontConfig.storefrontExperience.useGridProductDisplay) {
      return <ProductListingGridElement
        product={product}
        key={product.keyPath()}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
        updateCart={updateCart}
      />;
    } else {
      return <ProductListing
        product={product}
        key={product.keyPath()}
      />;
    }
  });

  const categorySectionClassNames = storefrontConfig.storefrontExperience.useGridProductDisplay ? "space-pb-none has-lowered-contrast" : "has-lowered-contrast-above-tablet";
  const productListClassNames = storefrontConfig.storefrontExperience.useGridProductDisplay ? "product-grid is-category-preview" : "container";

  const myID = category.encodedKeyPath();

  if (storefrontConfig.hideAgeRestrictedItems && category.onlyContainsAgeRestrictedItems()) return null;

  return <section className={categorySectionClassNames} id={myID} ref={elementRef}>
    <div className="container has-lowered-contrast">
      <div className="catalog-section-heading">
        <div className="title">
          <h3>{category.title}</h3>

          <button className="button is-icon-only has-large-icon is-hidden-above-tablet" aria-label="See all" onClick={(e) => onCategoryNavigate(e, category)}>
            <ArrowRightIcon className="icon streamline-icon"/>
          </button>

          <button className="chip is-action is-visible-above-tablet" onClick={(e) => onCategoryNavigate(e, category)}>
            <span>See all</span>
            <ArrowRightIcon className="icon streamline-icon"/>
          </button>
        </div>
      </div>
    </div>

    <div className={productListClassNames}>
      {products}
    </div>
  </section>
}

const CategoryLeafComponent = ({
  category,
  addToCart,
  removeFromCart,
  updateCart,
  availabilityLabel
}: {
  category: CategoryLeaf;
  addToCart?: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
  removeFromCart: (itemToRemove: CM.CartItem) => Promise<CM.Cart>;
  updateCart: (updatedCartItem: CM.CartItem) => Promise<CM.Cart>;
  availabilityLabel?: string;
}): React.JSX.Element => {
  const storefrontConfig = useStorefrontConfig();
  const observer = useCategoryObserver();

  const products = category.products.map(product => {
    if (storefrontConfig.hideAgeRestrictedItems && product.restrictions?.includes("age_r18")) return null;

    if (storefrontConfig.storefrontExperience.useGridProductDisplay) {
      return <ProductListingGridElement
        product={product}
        key={product.keyPath()}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
        updateCart={updateCart}
      />;
    } else {
      return <ProductListing
        product={product}
        key={product.keyPath()}
      />;
    }
  });

  const elementRef = useRef(null);
  useEffect(() => {
    const element = elementRef.current;

    if (element != null && observer != null)
      observer.observe(element);

    return () => {
      if (element != null && observer != null)
        observer.unobserve(element);
    }
  }, [elementRef, observer]);

  const myID = category.encodedKeyPath();

  const categorySectionClassNames = storefrontConfig.storefrontExperience.useGridProductDisplay ? "space-pb-none has-lowered-contrast" : "has-lowered-contrast-above-tablet";
  const productListClassNames = storefrontConfig.storefrontExperience.useGridProductDisplay ? "product-grid" : "container";

  if (storefrontConfig.hideAgeRestrictedItems && category.onlyContainsAgeRestrictedItems()) return <></>;

  return (
    <section className={categorySectionClassNames} id={myID} ref={elementRef}>
      <div className="container has-lowered-contrast">
        <div className="catalog-section-heading">
          <div className="title">
            <h3>{category.title}</h3>
          </div>

          {availabilityLabel && <p className="is-tiny-muted-text">{availabilityLabel}</p>}
          <p className="is-tiny-muted-text">{category.description}</p>
        </div>
      </div>

      <div className={productListClassNames}>
        {products}
      </div>

    </section>
  );
}

export const CategoryBranchComponent = ({
  category,
  addToCart,
  removeFromCart,
  updateCart,
  availabilityLabel
}: {
  category: CategoryBranch;
  addToCart?: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
  removeFromCart: (itemToRemove: CM.CartItem) => Promise<CM.Cart>;
  updateCart: (updatedCartItem: CM.CartItem) => Promise<CM.Cart>;
  availabilityLabel?: string;
}): React.JSX.Element => {
  const config = useStorefrontConfig();

  const children = useMemo(() => {
    if (config.usesSubcategoryDisplay) {
      return category.categories.map(cat =>
        <CategoryPreview
          key={cat.keyPath()}
          category={cat}
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          updateCart={updateCart}
        />
      )
    } else {
      return category.categories.map(cat =>
        <CategoryListing
          category={cat}
          key={cat.keyPath()}
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          updateCart={updateCart}
          availabilityLabel={availabilityLabel}
        />
      );
    }
  }, [category, config, addToCart, removeFromCart, updateCart, availabilityLabel]);

  return <>{children}</>;
}

export const CategoryListing = ({
  category,
  addToCart,
  removeFromCart,
  updateCart,
  availabilityLabel
}: {
  category: Category;
  addToCart?: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
  removeFromCart: (itemToRemove: CM.CartItem) => Promise<CM.Cart>;
  updateCart: (updatedCartItem: CM.CartItem) => Promise<CM.Cart>;
  availabilityLabel?: string;
}): React.JSX.Element => {
  const children = () => {
    if (category.kind === "branch") {
      return <CategoryBranchComponent
        category={category}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
        updateCart={updateCart}
        availabilityLabel={availabilityLabel}
      />
    } else {
      return <CategoryLeafComponent
        category={category}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
        updateCart={updateCart}
        availabilityLabel={availabilityLabel}
      />
    }
  };

  return (<>{children()}</>);
}
